/**
 * Организации мессенджера (03.10.2026). Человека видно в списках (пересылка, новый чат,
 * участники групп, @-упоминания), только если у вас есть общая организация, где оба
 * подтверждены. Принадлежность подтверждает владелец организации — тот, кто её завёл.
 * Сервер: backend/app/routers/orgs.py, правила — backend/app/services/orgs.py.
 */
import { api } from "@/api/client";

export type OrgStatus = "member" | "pending" | null;

export interface Org {
  id: number;
  name: string;
  is_home: boolean;
  is_owner: boolean;
  /** Назначен владельцем: сам принимает заявки и убирает участников. */
  is_admin: boolean;
  owner_name: string | null;
  status: OrgStatus;
  /** Сотрудник КБ в «Кирпичных беседках» — без заявки, выйти нельзя. */
  implicit: boolean;
  members: number;
  /** Заявок ждёт (только владельцу). */
  pending: number;
  /** Компания brixcrm этой организации (04.10.2026): её разделы открываются в её CRM. */
  account?: string | null;
}

export interface OrgMember {
  user_id: string;
  display_name: string | null;
  avatar_filename: string | null;
  email: string | null;
  is_owner: boolean;
  is_admin: boolean;
  implicit: boolean;
  requested_at: string | null;
  /** Учётка выключена через организацию (видят только управляющие). */
  disabled?: boolean;
  can_disable?: boolean;
  can_enable?: boolean;
}

export interface OrgMembers {
  org: Org;
  members: OrgMember[];
  pending: OrgMember[];
}

export const ORGS_KEY = ["orgs/my"] as const;

export const fetchMyOrgs = (): Promise<{ orgs: Org[]; pending_total: number }> =>
  api("/api/orgs/my");
export const searchOrgs = (q: string): Promise<Org[]> =>
  api(`/api/orgs/search?q=${encodeURIComponent(q)}`);
export const createOrg = (name: string): Promise<Org> =>
  api("/api/orgs", { method: "POST", body: JSON.stringify({ name }) });
export const joinOrg = (id: number): Promise<{ status: OrgStatus }> =>
  api(`/api/orgs/${id}/join`, { method: "POST" });
export const leaveOrg = (id: number): Promise<void> =>
  api(`/api/orgs/${id}/membership`, { method: "DELETE" });
export const fetchOrgMembers = (id: number): Promise<OrgMembers> =>
  api(`/api/orgs/${id}/members`);
export const approveOrgRequest = (id: number, userId: string): Promise<OrgMembers> =>
  api(`/api/orgs/${id}/requests/${encodeURIComponent(userId)}/approve`, { method: "POST" });
export const rejectOrgRequest = (id: number, userId: string): Promise<OrgMembers> =>
  api(`/api/orgs/${id}/requests/${encodeURIComponent(userId)}/reject`, { method: "POST" });
export const removeOrgMember = (id: number, userId: string): Promise<OrgMembers> =>
  api(`/api/orgs/${id}/members/${encodeURIComponent(userId)}`, { method: "DELETE" });
/** Выключить / включить учётку целиком (как «выключенный» в CRM). */
export const disableOrgAccount = (id: number, userId: string): Promise<OrgMembers> =>
  api(`/api/orgs/${id}/members/${encodeURIComponent(userId)}/disable`, { method: "POST" });
export const enableOrgAccount = (id: number, userId: string): Promise<OrgMembers> =>
  api(`/api/orgs/${id}/members/${encodeURIComponent(userId)}/enable`, { method: "POST" });
export const renameOrg = (id: number, name: string): Promise<Org> =>
  api(`/api/orgs/${id}`, { method: "PATCH", body: JSON.stringify({ name }) });
export const makeOrgAdmin = (id: number, userId: string): Promise<OrgMembers> =>
  api(`/api/orgs/${id}/admins/${encodeURIComponent(userId)}`, { method: "POST" });
export const unmakeOrgAdmin = (id: number, userId: string): Promise<OrgMembers> =>
  api(`/api/orgs/${id}/admins/${encodeURIComponent(userId)}`, { method: "DELETE" });

/** Кого владелец/администратор может добавить сам: по имени — из тех, кого видит; по точной почте/логину — любого. */
export interface OrgCandidate {
  user_id: string;
  display_name: string | null;
  avatar_filename: string | null;
  via: "contact" | "login";
}
export const fetchOrgCandidates = (id: number, q: string): Promise<OrgCandidate[]> =>
  api(`/api/orgs/${id}/candidates?q=${encodeURIComponent(q)}`);
export const addOrgMember = (id: number, body: { user_id?: string; login?: string }): Promise<OrgMembers> =>
  api(`/api/orgs/${id}/members`, { method: "POST", body: JSON.stringify(body) });

/** Организации человека в карточке профиля (PublicUser.orgs). */
export interface UserOrg {
  id: number;
  name: string;
  is_home: boolean;
  role: "owner" | "admin" | "member";
}
export const orgsLine = (orgs: UserOrg[] | null | undefined): string =>
  (orgs ?? [])
    .map(o => (o.role === "owner" ? `${o.name} · владелец` : o.role === "admin" ? `${o.name} · администратор` : o.name))
    .join(", ");

/** Чьи чаты (04.10.2026): {topic_id: org_id} — где организация указана явно; -1 — личный без организации.
 *  Чата нет в карте — он «Кирпичных беседок» (личка и «Избранное» — ничьи). Пометки — lib/topicOrgs.ts. */
export const TOPIC_ORGS_KEY = ["orgs/topic-map"] as const;
export const fetchTopicOrgs = (): Promise<{ topics: Record<string, number> }> => api("/api/orgs/topic-map");
/** Перенести группу в другую организацию: все её участники должны в ней состоять (иначе 409 с именами). */
export const setTopicOrg = (topicId: number, orgId: number): Promise<{ ok: boolean; org_id: number }> =>
  api(`/api/orgs/topics/${topicId}/org`, { method: "POST", body: JSON.stringify({ org_id: orgId }) });

export const deleteOrg = (id: number): Promise<void> =>
  api(`/api/orgs/${id}`, { method: "DELETE" });

/** Открытая регистрация в мессенджере. После неё — обычный вход тем же паролем. */
export const messengerSignup = (p: {
  display_name: string;
  email: string;
  password: string;
  website?: string;
}): Promise<{ ok: boolean; email: string }> =>
  api("/api/messenger/signup", { method: "POST", body: JSON.stringify(p) });
